import { queryOptions, type QueryClient } from "@tanstack/react-query";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { chatRequest, mergeMessages } from "./client";
import type { Identity, Message, Snapshot } from "../types";

export const HISTORY_LIMIT = 400;
export const CHAT_CACHE_MS = 60_000;
export const chatKey = (identity: Identity, chatId: string) =>
  ["webchat", identity.clientId, identity.memberId, chatId] as const;

/** Keep a readable bounded window; an earlier window has an explicit latest action. */
export function historyWindow(
  snapshot: Snapshot,
  incoming: Message[],
  direction: "sync" | "older" = "sync",
): Snapshot {
  const upper = snapshot.historyWindow
    ? snapshot.messages.at(-1)?.rootId
    : undefined;
  const eligible =
    upper && direction === "sync"
      ? incoming.filter((message) => BigInt(message.rootId) <= BigInt(upper))
      : incoming;
  const merged = mergeMessages(snapshot.messages, eligible);
  if (direction === "older" || snapshot.historyWindow)
    return {
      ...snapshot,
      messages: merged.slice(0, HISTORY_LIMIT),
      historyWindow: snapshot.historyWindow || merged.length > HISTORY_LIMIT,
    };
  const messages = merged.slice(-HISTORY_LIMIT);
  return {
    ...snapshot,
    messages,
    before:
      merged.length > HISTORY_LIMIT ? messages[0].rootId : snapshot.before,
  };
}

export function chatQueryOptions(
  client: QueryClient,
  identity: Identity,
  chatId: string,
) {
  const key = chatKey(identity, chatId);
  return queryOptions({
    queryKey: key,
    staleTime: 0,
    gcTime: CHAT_CACHE_MS,
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }): Promise<Snapshot> => {
      const cached = client.getQueryData<Snapshot>(key);
      if (!cached)
        return chatRequest<Snapshot>(
          API_ENDPOINTS.webchat.history(chatId),
          "GET",
          undefined,
          signal,
        );
      let result = cached;
      // Each pass yields after a bounded number of pages. The connection's dirty
      // loop resumes immediately until the authoritative cursor is current.
      for (let page = 0; page < 20; page++) {
        const next = await chatRequest<Snapshot>(
          `${API_ENDPOINTS.webchat.sync(chatId)}?cursor=${result.cursor}`,
          "GET",
          undefined,
          signal,
        );
        if (next.hasMore && next.cursor === result.cursor)
          throw new Error("Conversation sync did not advance. Please retry.");
        result = historyWindow(
          {
            ...next,
            before: result.before,
            historyWindow: result.historyWindow,
            messages: result.messages,
          },
          next.messages,
        );
        if (!next.hasMore) break;
      }
      return result;
    },
  });
}
